import { Outlet, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../ReduxToolkit/Hooks'
import Header from './Header/Header'
import Loader from './Loader/Loader'
import Sidebar from './Sidebar/Sidebar'
import TapTop from './TapTop/TapTop'
import ThemeCustomizer from './ThemeCustomizer/ThemeCustomizer'
import { setToggleSidebar } from '../ReduxToolkit/Reducers/LayoutSlice'
import { useEffect, useCallback, useRef } from 'react'
import { setLayout } from '../ReduxToolkit/Reducers/ThemeCustomizerSlice'
import TaskNotifier from '../Componant/Dashboard/Tasks/TaskNotifier'
import { DESKTOP_MIN_WIDTH, loadCollapsed } from './sidebarPreference'

const MOBILE_BREAKPOINT = 992;

const Layout = () => {
  const { layout } = useAppSelector((state) => state.themeCustomizer)
  const { toggleSidebar } = useAppSelector((state) => state.layout)
  const dispatch = useAppDispatch()
  const location = useLocation()

  // Last screen class seen (desktop or not). Resizes only matter when they
  // cross the breakpoint — otherwise every resize would undo the user's choice.
  const lastDesktop = useRef<boolean | null>(null)

  const compactSidebar = useCallback(() => {
    const windowWidth = window.innerWidth
    if (layout === 'compact-wrapper') {
      const desktop = windowWidth >= DESKTOP_MIN_WIDTH
      if (lastDesktop.current === desktop) return
      lastDesktop.current = desktop
      // Desktop: the user's last choice (expanded if they never chose).
      // Below desktop: closed off-canvas drawer, opened via the hamburger.
      dispatch(setToggleSidebar(desktop ? loadCollapsed() ?? false : true))
    } else if (layout === 'horizontal-wrapper') {
      if (windowWidth < MOBILE_BREAKPOINT) {
        dispatch(setToggleSidebar(true))
        dispatch(setLayout('compact-wrapper'))
      } else {
        dispatch(setToggleSidebar(false))
        dispatch(setLayout(localStorage.getItem('layout')))
      }
    }
  }, [dispatch, layout])

  useEffect(() => {
    lastDesktop.current = null // layout changed: re-apply once
    compactSidebar()
    window.addEventListener('resize', compactSidebar)
    return () => window.removeEventListener('resize', compactSidebar)
  }, [compactSidebar])

  // Close off-canvas sidebar after route changes on mobile
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT && !toggleSidebar) {
      dispatch(setToggleSidebar(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on pathname change
  }, [location.pathname])

  return (
    <>
      <Loader />
      <TaskNotifier />
      <TapTop />
      <div
        className={`page-wrapper ${layout}${!toggleSidebar ? ' sidebar-open' : ' sidebar-closed'}`}
      >
        <Header />
        <div className="page-body-wrapper">
          <Sidebar />
          {/* Backdrop: visible on mobile only when drawer is open (see index.scss) */}
          <div
            className="bg-overlay admin-mobile-overlay"
            aria-hidden={toggleSidebar}
            onClick={() => {
              if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT) {
                dispatch(setToggleSidebar(true))
              }
            }}
          />
          <Outlet />
        </div>
      </div>
      <ThemeCustomizer />
    </>
  )
}

export default Layout
